import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spin, Image, message } from 'antd';
import { CopyOutlined, CustomerServiceOutlined } from '@ant-design/icons';
import api from '../services/api';

const TEAL = '#1A6B54';
const GOLD = '#B8892A';
const INK = '#18140C';
const MUTED = '#786A56';
const BD = '#ECE5D8';

const REGION_ORDER = ['japan', 'china', 'singapore', 'hongkong', 'global'];
const REGION_FLAG = { japan: '🇯🇵', china: '🇨🇳', singapore: '🇸🇬', hongkong: '🇭🇰', global: '🌐' };

export default function SupportCenter() {
  const { t } = useTranslation();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/settings/support-contacts')
      .then(({ data }) => setContacts(data.contacts || []))
      .catch(() => setContacts([]))
      .finally(() => setLoading(false));
  }, []);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(t('support.copied'));
    } catch {
      message.info(text);
    }
  };

  // 按地区分组(仅显示有客服的地区)
  const groups = REGION_ORDER
    .map(r => ({ region: r, items: contacts.filter(c => c.region === r) }))
    .filter(g => g.items.length > 0);
  // 未识别地区兜底
  const known = new Set(REGION_ORDER);
  const others = contacts.filter(c => !known.has(c.region));
  if (others.length) groups.push({ region: 'global', items: others });

  return (
    <div style={{ minHeight: '100vh', background: '#FAF8F5', paddingBottom: 40 }}>
      {/* 头部 */}
      <div style={{ background: 'linear-gradient(135deg, #1A2E28 0%, #1F3A32 60%, #2A4A3E 100%)', padding: '28px 20px 24px', textAlign: 'center' }}>
        <div style={{ width: 52, height: 52, borderRadius: 14, margin: '0 auto 12px', background: 'rgba(255,255,255,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CustomerServiceOutlined style={{ fontSize: 26, color: '#E0B65C' }} />
        </div>
        <div style={{ fontSize: 19, fontWeight: 700, color: '#fff' }}>{t('support.title')}</div>
        <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.7)', marginTop: 6, lineHeight: 1.6, maxWidth: 320, marginLeft: 'auto', marginRight: 'auto' }}>
          {t('support.subtitle')}
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '16px 14px 0' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
        ) : groups.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: MUTED }}>
            <div style={{ fontSize: 34, marginBottom: 12 }}>🛎️</div>
            <div style={{ fontSize: 14 }}>{t('support.empty')}</div>
          </div>
        ) : groups.map(g => (
          <div key={g.region} style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: INK, margin: '4px 2px 10px', display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ fontSize: 16 }}>{REGION_FLAG[g.region] || '🌐'}</span>
              {t('support.region.' + g.region)}
              <span style={{ fontSize: 11, color: MUTED, fontWeight: 400 }}>· {g.items.length}</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 12 }}>
              {g.items.map((c, i) => (
                <div key={c.id || i} style={{ background: '#fff', border: `1px solid ${BD}`, borderRadius: 12, padding: 14, display: 'flex', gap: 14 }}>
                  {/* 二维码 */}
                  {c.qr_url ? (
                    <div style={{ textAlign: 'center', flexShrink: 0 }}>
                      <Image src={c.qr_url} width={92} height={92} style={{ objectFit: 'cover', borderRadius: 8, border: `1px solid ${BD}` }} />
                      <div style={{ fontSize: 10, color: GOLD, marginTop: 4 }}>{t('support.tapEnlarge')}</div>
                    </div>
                  ) : null}
                  {/* 信息 */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: INK }}>{c.name || t('support.official')}</div>
                    {c.wechat_id && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                        <span style={{ fontSize: 11, color: MUTED }}>{t('support.wechatId')}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: TEAL, fontFamily: 'monospace', wordBreak: 'break-all' }}>{c.wechat_id}</span>
                        <button onClick={() => copy(c.wechat_id)} style={{
                          border: `1px solid ${TEAL}`, background: 'transparent', color: TEAL, borderRadius: 5,
                          fontSize: 11, padding: '2px 8px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0,
                        }}><CopyOutlined /> {t('support.copy')}</button>
                      </div>
                    )}
                    {c.hours && <div style={{ fontSize: 12, color: MUTED, marginTop: 6 }}>🕐 {c.hours}</div>}
                    {c.note && <div style={{ fontSize: 12, color: MUTED, marginTop: 3 }}>{c.note}</div>}
                    {c.qr_url && <div style={{ fontSize: 11, color: '#A8998A', marginTop: 8, lineHeight: 1.5 }}>{t('support.scanHint')}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
